import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

interface CountryOption {
  code: CountryCode;
  dialCode: string;
  name: string;
  flag: string;
}

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

function toFlag(code: string): string {
  return [...code].map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0))).join('');
}

const COUNTRIES: CountryOption[] = getCountries()
  .map((code) => ({
    code,
    dialCode: String(getCountryCallingCode(code)),
    name: regionNames.of(code) ?? code,
    flag: toFlag(code),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const COUNTRY_MAP = new Map(COUNTRIES.map((c) => [c.code, c]));

@Component({
  selector: 'app-phone-input',
  standalone: true,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule],
  templateUrl: './phone-input.html',
  styleUrl: './phone-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
})
export class PhoneInputComponent implements ControlValueAccessor, Validator {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);

  protected readonly countries = COUNTRIES;
  protected readonly countryMap = COUNTRY_MAP;

  protected readonly internal = this.fb.nonNullable.group({
    country: 'ZA' as CountryCode,
    number: '',
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChangeFn: (value: string | null) => void = (_: string | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouchedFn: () => void = () => {};

  constructor() {
    this.internal.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.emitChange();
      this.cdr.markForCheck();
    });
  }

  private emitChange(): void {
    const { country, number } = this.internal.getRawValue();
    if (!number.trim()) {
      this.internal.controls.number.setErrors(null);
      this.onChangeFn(null);
      return;
    }
    const cc = country as CountryCode;
    if (isValidPhoneNumber(number, cc)) {
      this.internal.controls.number.setErrors(null);
      try {
        this.onChangeFn(parsePhoneNumberWithError(number, { defaultCountry: cc }).format('E.164'));
      } catch {
        this.onChangeFn(null);
      }
    } else {
      this.internal.controls.number.setErrors({ mobileFormat: true });
      this.onChangeFn(null);
    }
  }

  protected onBlur(): void {
    this.onTouchedFn();
  }

  writeValue(e164: string | null): void {
    if (!e164) {
      this.internal.setValue({ country: 'ZA' as CountryCode, number: '' }, { emitEvent: false });
      return;
    }
    try {
      const parsed = parsePhoneNumberWithError(e164);
      this.internal.setValue(
        { country: (parsed.country ?? 'ZA') as CountryCode, number: parsed.nationalNumber },
        { emitEvent: false },
      );
    } catch {
      this.internal.setValue({ country: 'ZA' as CountryCode, number: '' }, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.internal.disable();
    } else {
      this.internal.enable();
    }
  }

  validate(_: AbstractControl): ValidationErrors | null {
    const { country, number } = this.internal.getRawValue();
    if (!number.trim()) return null;
    try {
      return isValidPhoneNumber(number, country as CountryCode) ? null : { mobileFormat: true };
    } catch {
      return { mobileFormat: true };
    }
  }
}
